import { useCallback, useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { TextInput } from '@components/shared/form/TextInput';
import { useFormContext } from '@context/FormContext';
import { Screens } from '@shared/constants/screens';
import useForm, { validate } from '@shared/hooks/useForm';
import { useProve } from '@context/proveProvider';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { createContact } from '@services/verifyYourInformation';
import { ApiResources, createErrorResponse, formatPostalCode, getContactData } from '@services/utils';
import { challenge, complete } from '@services/prove';
import { setLocalStorage } from 'utils/setLocalStorage';
import { convertArrayToSnakeCase, toDefaultPhoneFormat } from 'utils/stringFormat';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { Address, IProveChallengeRequest } from 'types/proveIdentity';
import { AddressInfo, CDX_ACTIONS, UpdateAddressInfo } from 'types/form';
import { SECONDARY_SOURCE, UNIDENTIFIED_SOURCE } from '@shared/constants/utm';
import { AssetLinks } from '@assets/assets';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { RESIDENT_TYPE_OPTIONS } from '@shared/constants/misc';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

interface IVerifyYourInformationPartial {
  email: string;
  residency: string;
}

const YourInfoForm = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  const { t } = useTexts();
  const { trackClick, trackFormSubmit, trackPage, identifyUser, startInteraction } = useAnalytics(
    SEGMENT_SCREEN_PARAMS.CONTACT_INFO
  );

  const { correlationId, nextStep, challengeMissing, getNextEndpoint } = useProve();
  const { next } = useScreenNavigator();

  const {
    currentStep,
    licensePlateInfoForm,
    manualLicensePlateInfoForm,
    verifyYourInformation,
    verifyYourIdentity,
    licensePlateInfoResponse,
    handleStep,
    updateVerifyYourInformationForm,
    isLoading,
    dispatch,
    submitStart,
    submitSuccess,
    widgetInitVariables,
    contactDetails
  } = useFormContext();
  const { firstName, lastName, addresses, ssn, dateOfBirth, email } = verifyYourInformation;
  const contactData = getContactData();

  const fetchChallenge = useCallback(async () => {
    if (correlationId && correlationId?.length > 0) {
      let requestPayload = {} as IProveChallengeRequest;
      if (challengeMissing) {
        requestPayload = {
          dob: verifyYourIdentity.dateOfBirth,
          correlationId,
        };
      } else {
        requestPayload = {
          correlationId,
        };
      }
      submitStart();
      setShowSpinner(true);
      const challengeResponse = await challenge(requestPayload);

      if ('error' in challengeResponse) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: challengeResponse,
        });
        setShowSpinner(false);
      } else {
        if (challengeResponse) {
          if (challengeResponse.individual) {
            const { firstName, lastName, ssn, emailAddresses, dob } = challengeResponse?.individual ?? {};
            const emailId = emailAddresses?.[0] ?? '';
            let updatedAddress = [];
            let currentAddresses = null;

            if (!challengeResponse?.individual?.addresses || challengeResponse?.individual?.addresses.length === 0 && contactData?.leadSourceId) {
              if (Array.isArray(contactData?.addresses) && contactData?.addresses?.length > 0) {
                 currentAddresses = contactData?.addresses
                  .filter(({ isCurrent }: AddressInfo) => isCurrent)
                  .map(({ streetLine1, streetLine2, city, state, zip }: AddressInfo) => ({
                    address: streetLine1,
                    city,
                    extendedAddress: streetLine2 ?? null,
                    postalCode: zip,
                    region: state,

                  }));
                updatedAddress = currentAddresses;
              }
            } else {
              updatedAddress = challengeResponse?.individual?.addresses;
            }
            const verifyInfo = {
              firstName: firstName ? firstName : verifyYourInformation.firstName,
              lastName: lastName ? lastName : verifyYourInformation.lastName,
              addresses: updatedAddress,
              phone: verifyYourIdentity?.phone ?? '',
              ssn: ssn ? ssn : verifyYourInformation.ssn,
              dateOfBirth: dob ? dob : dateOfBirth,
              email: emailId ? emailId : verifyYourInformation?.email,
              residency: verifyYourInformation.residency || '' ,
            };
            submitSuccess();
            handleInputChange('email', emailId);
            updateVerifyYourInformationForm(verifyInfo);
            setShowSpinner(false);
            const individual = challengeResponse?.individual;
            const validations = [
              {
                condition: !individual?.addresses || individual.addresses.length === 0,
                segmentTrackMessage: 'Personal Information: address not present from prove',
              },
              {
                condition: !individual?.firstName,
                segmentTrackMessage: 'Personal Information: first name not present from prove',
              },
              {
                condition: !individual?.lastName,
                segmentTrackMessage: 'Personal Information: last name not present from prove',
              },
              {
                condition: !individual?.ssn,
                segmentTrackMessage: 'Personal Information: SSN not present from prove',
              },
            ];

            const failedValidation = validations.find((v) => v.condition);

            if (failedValidation) {
              next(currentStep, 'postSubmitOutcome', { failedValidation }, false);
              trackFormSubmit('Identity Verification Completed', {
                verification_status: false,
                verification_provider: 'prove',
                failure_reason: 'mismatch',
              });
            }
          } else {
            submitSuccess();
            trackFormSubmit('Identity Verification Completed', {
              verification_status: false,
              verification_provider: 'prove',
              failure_reason: 'mismatch',
            });
            next(currentStep, 'postSubmitOutcome', { challengeResponse }, false);
            setShowSpinner(false);
          }
        }
      }
    }
  }, [correlationId]);

  useEffect(() => {
    processUserVerification();
  }, []);

  const processUserVerification = async () => {
    const stepKey = nextStep ? Object.keys(nextStep)[0] : '';
    const nextEndpoint = getNextEndpoint(stepKey);
    if (nextEndpoint === ApiResources.proveChallenge) {
      if (correlationId && correlationId?.length > 0) {
        fetchChallenge();
      }
    }
    next(currentStep, 'onLoad', { nextEndpoint }, false);
  };

  const { errors, handleChange, handleSubmit } = useForm<IVerifyYourInformationPartial>({
    initialValues: {
      email,
      residency: '',
    },
    validationSchema: {
      email: validate
        .string('email')
        .required(t('yourInformationManually.validation.email.required'))
        .email(t('yourInformationManually.validation.email.validEmail')),
      residency: validate.string('residency').required(t('yourInformationManually.validation.residency.required')),
    },
    onSubmit: async () => {
      submitStart();
      try {
        const completeResponse = await complete({
          individual: {
            firstName: firstName,
            lastName: lastName,
            ssn: ssn,
            addresses: addresses,
            emailAddresses: [email],
            dob: dateOfBirth,
          },
          correlationId: correlationId as string,
        });

        if ('error' in completeResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: completeResponse,
          });
        } else {
          if (completeResponse && completeResponse.success) {
            try {
              const urlParams = widgetInitVariables?.tracking?.extraData && JSON.parse(widgetInitVariables?.tracking?.extraData);

              const defaultUtm = {
                primarySourceId: widgetInitVariables?.PartnerName || urlParams?.partner_name || UNIDENTIFIED_SOURCE,
                secondarySourceId: SECONDARY_SOURCE,
              };
              trackClick('UTM Parameters', defaultUtm);

              const deviceType = window.innerWidth <= 500 ? 'mobile' : 'desktop';
              const metaData = {
                version: '1.0',
                loanApplicationId: urlParams?.loan_application_id || '',
                partnerName: widgetInitVariables?.PartnerName || urlParams?.partner_name || UNIDENTIFIED_SOURCE,
                secondaryLeadSource: SECONDARY_SOURCE,
                utm: {
                  utm_medium: urlParams?.utm_medium || '',
                  utm_campaign: urlParams?.utm_campaign || '',
                  utm_content: urlParams?.utm_content || '',
                  secondary_source: urlParams?.secondary_source || '',
                  gclid: urlParams?.gclid || '',
                  subid: urlParams?.subid || '',
                  clickref: urlParams?.clickref || '',
                  search_id: urlParams?.search_id || '',
                  oneToOneConsent: 'true',
                  subid1: urlParams?.subid1 || '',
                  subid2: urlParams?.subid2 || '',
                  subid3: deviceType || '',
                  sharedid: urlParams?.sharedid || '',
                  clickid: urlParams?.clickid || '',
                  arm_enabled: urlParams?.arm_enabled || '',
                  utm_source: urlParams?.utm_source || '',
                  irclickid: urlParams?.irclickid || '',
                },
              };

              const createContactResponse = await createContact(
                contactDetails?.contactId ||  null,
                contactDetails?.leadId ||  null,
                contactDetails?.assetId ||  null,
                licensePlateInfoForm,
                manualLicensePlateInfoForm,
                verifyYourInformation,
                verifyYourIdentity,
                licensePlateInfoResponse,
                metaData,
                correlationId || null
              );
              const userObject = {
                email: email,
                first_name: firstName,
                last_name: lastName,
                phone: verifyYourInformation?.phone,
                loan_application_id: urlParams?.loan_application_id || '',
                resident_status: verifyYourInformation?.residency,
                plateState: licensePlateInfoForm.plateState,
                plateNumber: licensePlateInfoForm.plateNumber,
                estimatedMileageRange: licensePlateInfoForm.estimatedMileageRange || manualLicensePlateInfoForm.estimatedMileage,
                vin: licensePlateInfoResponse?.vin ?? '',
                year: manualLicensePlateInfoForm.year,
                make: manualLicensePlateInfoForm.makeName || manualLicensePlateInfoForm.make,
                model: manualLicensePlateInfoForm.model,
              };
              if ('error' in createContactResponse) {
                trackFormSubmit('Form Submitted', {
                  submission_status: false,
                  value: 0,
                  currency: 'USD',
                  ...userObject,
                  ...defaultUtm,
                });
                dispatch({
                  type: CDX_ACTIONS.SUBMIT_API_ERROR,
                  payload: createContactResponse,
                });
              } else {
                dispatch({ type: 'SUBMIT_CREATE_CONTACT_SUCCESS', payload: createContactResponse ?? null });
                let updatedAddress: UpdateAddressInfo[] = [];
                if (Array.isArray(verifyYourInformation?.addresses) && verifyYourInformation?.addresses?.length > 0) {
                  const addressFromStore = verifyYourInformation?.addresses;
                  updatedAddress = addressFromStore.map((addr: Address, index: number) => ({
                    streetLine1: addr?.address ?? '',
                    streetLine2: addr?.extendedAddress ?? '',
                    city: addr?.city,
                    state: addr?.region,
                    zip: addr?.postalCode,
                    type: '',
                    monthlyPayment: 0,
                    monthsAtAddress: 0,
                    unit: '',
                    county: null,
                    isCurrent: index === 0 ? true : false,
                  }));
                }
                const contactData = {
                  addresses: updatedAddress,
                  contactId: createContactResponse.contactId,
                  leadId: createContactResponse.leadId,
                  assetId: createContactResponse?.assetId,
                  timeStamp: new Date().toISOString(),
                };
                setLocalStorage(contactData, 'contactData');
                trackFormSubmit('Form Submitted', {
                  submission_status: true,
                  prefill_status: 'accepted',
                  forward_screen_name: 'loan_details',
                  ...userObject,
                  ...defaultUtm,
                });
                trackFormSubmit('Consent Update Provided', {
                  form_context: 'contact_info',
                  consent_type: 'add', // add|remove
                  consent_category: 'marketing',
                  one_to_one_consent: true,
                  submission_status: true,
                  ...userObject,
                  ...defaultUtm,
                });
                trackFormSubmit('Lead Created', {
                  prefill_status: 'accepted',
                  one_to_one_consent: true,
                  soft_pull_authorization_consent: true,
                  submission_status: true,
                  value: 150,
                  currency: 'USD',
                  ...userObject,
                  ...defaultUtm,
                });
                identifyUser(createContactResponse?.contactId, userObject);
                next(currentStep, 'postSubmitOutcome', { completeResponse }, true);

              }
            } catch (error) {
              dispatch({
                type: CDX_ACTIONS.SUBMIT_API_ERROR,
                payload: createErrorResponse(
                  error instanceof Error ? error.name : 400,
                  error instanceof Error ? error.message : ''
                ),
              });
            }
          } else {
            trackFormSubmit('Form Submitted', {
              submission_status: false,
              prefill_status: 'provided',
              submission_fail_reason: 'mismatch',
              value: 0,
              currency: 'USD',
            });
          }
        }
      } catch (error) {
        trackFormSubmit('Form Submitted', {
          submission_status: false,
          prefill_status: 'provided',
          submission_fail_reason: error instanceof Error ? error.message : '',
          value: 0,
          currency: 'USD',
        });
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : ''),
        });
      }
    },
    onValidationError: (errors) => {
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        prefill_status: 'provided',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  const handleInputChange = <K extends keyof IVerifyYourInformationPartial>(
    field: K,
    value: IVerifyYourInformationPartial[K]
  ) => {
    handleChange(field, value);
    updateVerifyYourInformationForm({ [field]: value });
  };

  const formatDateOfBirth = (dateOfBirth: string) => {
    const dob = dateOfBirth ? new Date(dateOfBirth) : '';
    return dob
      ? `${String(dob.getUTCMonth() + 1).padStart(2, '0')}/${String(dob.getUTCDate()).padStart(2, '0')}/${dob.getUTCFullYear()}`
      : '';
  };

  useEffectOnce(() =>
    trackPage(t('pageTitles.VerifyYourInformation'), { prefill_status: 'provided', prefill_source: 'credit_bureau' })
  );

  const handleClickEditPersonalInfo = () => {
    trackClick('CTA Clicked', {
      cta_id: 'edit_personal_information',
      cta_text: 'Edit personal information',
      cta_type: 'link',
      cta_context: 'prefill_summary',
      prefill_status: 'edit_requested',
    });
    handleStep(Screens.YOUR_INFORMATION_MANUALLY);
  };
  return (
    <>
      <div>
        <h1 className="iLendingCDX-form-title">{t('yourInformation.title')}</h1>

        <p className="iLendingCDX-form-title--small iLendingCDX-mb-8"> {t('yourInformation.pleaseReviewAndVerify')}</p>

        {
          <div className="iLendingCDX-InfoCard">
            <div className="iLendingCDX-InfoCard-container iLendingCDX-mb-3 ">
              <div className="iLendingCDX-InfoCard-info">
                <p>
                  {firstName} {lastName}
                </p>
                {addresses && addresses.length > 0 && (
                  <p>
                    {addresses[0]?.address} {addresses[0]?.city}, {addresses[0]?.region}{' '}
                    {formatPostalCode(addresses[0]?.postalCode)}
                  </p>
                )}
                <p>{toDefaultPhoneFormat(verifyYourIdentity?.phone)}</p>
                <p>{ssn}</p>
                <p>{formatDateOfBirth(dateOfBirth)}</p>
              </div>
            </div>
            <span
              className="iLendingCDX-InfoCard-edit"
              onClick={handleClickEditPersonalInfo}
            >
              <img
                src={AssetLinks.pencil}
                alt=""
              />
              {t('yourInformation.editPersonalInformation')}
            </span>
          </div>
        }
        <div className="iLendingCDX-footer">
          <span className="iLendingCDX-footer-divider--full"></span>
        </div>
        <h1 className="iLendingCDX-form-title">{t('yourInformation.title2')}</h1>

        <p className="iLendingCDX-form-title--small"> {t('yourInformation.enterCurrentEmail')}</p>
        <form
          onSubmit={handleSubmit}
          onFocus={startInteraction}
        >
          <div className="iLendingCDX-mb-6 iLendingCDX-mt-6">
            <TextInput
              label={t('yourInformation.form.email')}
              className={'iLendingCDX-mb-6'}
              name="email"
              value={verifyYourInformation.email}
              errorMessage={errors.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
            <CustomSelect
              label={t('yourInformation.form.residency')}
              className={'iLendingCDX-mb-6'}
              options={RESIDENT_TYPE_OPTIONS}
              value={verifyYourInformation.residency || ''}
              errorMessage={errors.residency}
              onChange={(e) => handleInputChange('residency', e.target.value)}
            />

            <label
              className="iLendingCDX-checkbox-wrap-container iLendingCDX-input-label iLendingCDX-input-label--check"
              htmlFor="consent-check"
            >
              {t('yourInformation.acknowledge')} {t('yourInformation.confirmText')}
              <a
                href="https://ilendingcarloanrefinancing.com/sms-terms-of-use"
                target="_blank"
                rel="noreferrer"
              >
                {t('yourInformation.smsTerms')}
                {','}
              </a>
              <a
                href="https://ilendingcarloanrefinancing.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                {t('yourInformation.privacyPolicy')}
                {','}
              </a>
              {t('yourInformation.and')}
              <a
                href="https://ilendingcarloanrefinancing.com/terms-of-use"
                target="_blank"
                rel="noreferrer"
              >
                {t('yourInformation.termsOfUse')}
              </a>
              {t('yourInformation.consentNotRequired')}
            </label>
          </div>
          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
            isLoading={isLoading}
          >
            {t('yourInformation.form.continue')}
          </Button>
        </form>
      </div>
      {showSpinner && <OverlaySpinner size="lg" />}
    </>
  );
};

export default YourInfoForm;
